/* eslint-disable @typescript-eslint/no-explicit-any */
import { apiClient } from "@/config/api";
import { LoginFormValues } from "@/lib/validations/auth";
import {
  LoginResponse,
  RefreshTokenResponse,
  StatusResponse,
} from "@/types/status";
import { getStatusMessage } from "@/utils/messageHelper";

// Custom error class để chứa StatusResponse
export class AuthError extends Error {
  constructor(
    public statusResponse: StatusResponse,
    message: string,
    public originalMessage?: string
  ) {
    super(message);
    this.name = "AuthError";
  }
}

export const AUTH_ENDPOINTS = {
  SIGN_IN: "/auth/sign-in",
  GET_USER_INFO: "/auth/me",
  REFRESH_TOKEN: "/auth/refresh-token",
  LOGOUT: "/auth/logout",
};

export const authService = {
  // Đăng nhập
  async login(data: LoginFormValues): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>(
      AUTH_ENDPOINTS.SIGN_IN,
      {
        username: data.username,
        password: data.password,
      }
    );
    if (!response.ok || !response.data) {
      throw new AuthError(
        response.data?.status || StatusResponse.FAIL,
        getStatusMessage(
          response.data?.status || StatusResponse.FAIL,
          "Đăng nhập thất bại"
        ),
        response.data?.message
      );
    }

    // Backend bọc payload trong { statusCode, message, data }, nhưng khi đăng nhập
    // thất bại nó vẫn trả HTTP 2xx với data rỗng -> phải tự phát hiện và báo lỗi.
    const payload = (response.data as any)?.data ?? response.data;

    if (!payload?.accessToken) {
      const status =
        payload?.status || response.data?.status || StatusResponse.FAIL;
      throw new AuthError(
        status,
        getStatusMessage(
          status,
          "Tên đăng nhập hoặc mật khẩu không chính xác"
        ),
        payload?.message
      );
    }

    return payload;
  },

  // Lấy thông tin người dùng từ token
  async getUserInfo(_token: string): Promise<any | null> {
    const response = await apiClient.get<any>(AUTH_ENDPOINTS.GET_USER_INFO);

    if (!response.ok || !response.data) {
      console.error("Error fetching user info:", response.statusText);
      return null;
    }

    // Backend trả về statusCode thay vì status
    if (response.data.statusCode !== 200) {
      console.error("Backend error:", response.data.message);
      return null;
    }

    // Trả về data.user hoặc data tùy theo cấu trúc response
    return response.data.data || response.data;
  },

  // Làm mới token
  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    const response = await apiClient.post<RefreshTokenResponse>(
      AUTH_ENDPOINTS.REFRESH_TOKEN,
      {
        refreshToken,
      }
    );

    if (!response.ok || !response.data) {
      throw new AuthError(
        response.data?.status || StatusResponse.FAIL,
        getStatusMessage(
          response.data?.status || StatusResponse.FAIL,
          "Làm mới token thất bại"
        ),
        response.data?.message
      );
    }

    // Kiểm tra status từ backend
    if (response.data.status !== StatusResponse.SUCCESS) {
      throw new AuthError(
        response.data.status,
        getStatusMessage(response.data.status, response.data.message),
        response.data.message
      );
    }

    return response.data;
  },

  // Đăng xuất
  async logout(token: string): Promise<any> {
    const response = await apiClient.post(
      AUTH_ENDPOINTS.LOGOUT,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok || !response.data) {
      throw new AuthError(
        response.data?.status || StatusResponse.FAIL,
        getStatusMessage(
          response.data?.status || StatusResponse.FAIL,
          "Đăng xuất thất bại"
        ),
        response.data?.message
      );
    }

    return response.data;
  },
};
